const steps = [
  {
    num: '01',
    icon: 'ri-search-2-line',
    title: 'We Analyze Your Business',
    desc: 'We review your current site, competitors, local market, and target keywords to build a clear growth strategy.',
    detail: 'Takes 24–48 hours',
  },
  {
    num: '02',
    icon: 'ri-layout-masonry-line',
    title: 'We Build Your New Website',
    desc: 'A modern, fast, conversion-focused website — up to 10 pages — built for AI search, voice, Google, and local maps.',
    detail: 'Delivered for review',
  },
  {
    num: '03',
    icon: 'ri-checkbox-circle-line',
    title: 'You Review & Approve',
    desc: 'You see your new site before anything goes live. We revise until it\'s exactly right. Nothing launches without your approval.',
    detail: 'Your sign-off required',
  },
  {
    num: '04',
    icon: 'ri-rocket-2-line',
    title: 'We Launch & Optimize Monthly',
    desc: 'We go live and run your SEO every single month — building authority, improving rankings, and compounding your results.',
    detail: 'Ongoing every month',
  },
];

interface HowItWorksSectionProps {
  onOpenForm: () => void;
}

export default function HowItWorksSection({ onOpenForm }: HowItWorksSectionProps) {
  return (
    <section id="how-it-works" className="bg-[#f8faf9] py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-emerald-500 text-xs font-bold tracking-widest uppercase mb-4">The Process</p>
          <h2 className="text-3xl md:text-5xl font-black text-[#0d1117] leading-tight mb-5">
            How It Works
          </h2>
          <p className="text-[#6b7280] text-base md:text-lg max-w-md mx-auto">
            Simple. Clear. Built around your approval every step of the way.
          </p>
        </div>

        {/* Steps — timeline style */}
        <div className="relative max-w-4xl mx-auto mb-14">
          {/* Vertical line */}
          <div className="absolute left-[27px] md:left-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-emerald-400 via-teal-300 to-emerald-200 opacity-30 hidden md:block md:-translate-x-px"></div>

          <div className="space-y-8">
            {steps.map((step, i) => (
              <div
                key={step.num}
                className={`relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10 ${
                  i % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                {/* Card */}
                <div className={`flex-1 bg-white border border-[#e5e7eb] rounded-2xl p-7 hover:border-emerald-200 hover:shadow-md transition-all duration-300 ${
                  i % 2 === 1 ? 'md:text-right' : ''
                }`}>
                  <div className={`flex items-center gap-3 mb-4 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
                      <i className={`${step.icon} text-emerald-600`} style={{ fontSize: '18px' }}></i>
                    </div>
                    <span className="text-emerald-400 text-xs font-black tracking-widest uppercase">{step.num}</span>
                  </div>
                  <h3 className="text-[#0d1117] font-black text-lg mb-2">{step.title}</h3>
                  <p className="text-[#6b7280] text-sm leading-relaxed mb-3">{step.desc}</p>
                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 text-emerald-600 text-xs font-semibold px-3 py-1 rounded-full">
                    <i className="ri-time-line" style={{ fontSize: '11px' }}></i>
                    {step.detail}
                  </span>
                </div>

                {/* Center node */}
                <div className="hidden md:flex w-14 h-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white font-black text-sm z-10 shadow-lg shadow-emerald-900/20">
                  {step.num}
                </div>

                {/* Empty space for alternating layout */}
                <div className="flex-1 hidden md:block"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-[#0d1117] to-[#0d2420] rounded-2xl px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-lg md:text-xl mb-1.5">Start with step 1 — free.</p>
            <p className="text-white/50 text-sm">We analyze your business and build your preview at no charge.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={onOpenForm}
              className="whitespace-nowrap bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity cursor-pointer flex items-center gap-2"
            >
              Get My Free Preview
              <i className="ri-arrow-right-line" style={{ fontSize: '15px' }}></i>
            </button>
            <a
              href="tel:8004559670"
              className="whitespace-nowrap flex items-center justify-center gap-2 bg-white/8 border border-white/15 text-white font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-white/14 transition-all cursor-pointer"
            >
              <i className="ri-phone-fill text-emerald-400" style={{ fontSize: '14px' }}></i>
              800-455-9670
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
